/* Investor Profile — ported from the prototype's Screens.profile(). Read-only
   mirror of the VC's Diligentsia investor settings: identity + Companies House,
   objective match criteria (rule-out / advisory), the five scoring weights,
   subjective criteria + thesis read-back, quick responses and preferred contact. */
import React from 'react';

import Bubble from '../components/Bubble';
import BarRow from '../components/BarRow';
import { useProfile } from '../state';
import { Loading, EmptyState } from '../components/States';

export default function Profile() {
	const { profile: P, loading, error } = useProfile();

	if (loading) return <main className="page screen"><Loading label="Loading investor profile…" /></main>;
	if (error || !P) return <main className="page screen"><EmptyState title="Investor profile not synced yet" sub="Your thesis, criteria and weights sync here from your Diligentsia investor settings. Once synced, every score, triage and memo is weighted against them." /></main>;

	const criteria = P.criteria || [];
	const weights = P.weights || [];
	const quickResponses = P.quickResponses || [];
	const preferredContact = P.preferredContact || {};
	const extended = P.extended || {};
	const ruleOutCount = criteria.filter(c => c.ruleOut).length;

	return (
		<main className="page screen">
			<div className="page__head">
				<div>
					<div className="kicker">Investor profile · pulled from Diligentsia settings</div>
					<div className="page__title">{P.short}</div>
					<div className="page__sub">Read-only here — every score, triage and memo is weighted against these settings.</div>
				</div>
				<div className="page__actions">
					<span className="chip chip--muted" style={{ padding: '7px 14px', fontSize: 12.5 }}>⟳ Synced 2h ago</span>
					<a className="pill pill--primary" href="https://diligentsia.co.uk/investor-setting" target="_blank" rel="noopener noreferrer">Edit on Diligentsia ↗</a>
				</div>
			</div>

			<div className="card idcard" style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 12 }}>
				<span className="dealstrip__logo" style={{ width: 52, height: 52, borderRadius: '50%', fontSize: 18 }}>{P.initials}</span>
				<div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
						<span style={{ fontSize: 17, fontWeight: 500 }}>{P.name}</span>
						<span className="chip chip--green">✓ Companies House · {P.chNumber}</span>
					</div>
					<div style={{ fontSize: 13, color: 'var(--dlg-muted)', marginTop: 3 }}>{P.website} · {P.hq} · {P.staffHome} staff ({P.staffGlobal} globally) · Preferred contact: {preferredContact.name}</div>
				</div>
				<div className="idcard__facts" style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
					<div className="factchip"><div className="factchip__label">Funds available</div><div className="factchip__value">{P.fundsAvailable}</div></div>
					<div className="factchip"><div className="factchip__label">Investments made</div><div className="factchip__value">{P.investmentsMade}</div></div>
					<div className="factchip"><div className="factchip__label">Last investment</div><div className="factchip__value">{P.lastInvestment} <span style={{ color: 'var(--dlg-pos)', fontSize: 12 }}>● active</span></div></div>
				</div>
			</div>

			<div className="grid grid--split45">
				<div className="card">
					<div className="card__row" style={{ marginBottom: 12 }}><div className="card__label">Match criteria · objective</div><span className="card__hint">Rule-outs feed the auto-decline gate</span></div>
					<div style={{ display: 'grid', gap: 9 }}>
						{criteria.map(c => (
							<div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5 }}>
								<span style={{ width: 170, color: 'var(--dlg-muted)' }}>{c.label}</span>
								<span style={{ fontWeight: 500 }}>{c.value}{c.extra ? <span style={{ color: 'var(--dlg-faint)', fontWeight: 400 }}> {c.extra}</span> : null}</span>
								<span className={`chip ${c.ruleOut ? 'chip--red' : 'chip--muted'}`} style={{ marginLeft: 'auto' }}>{c.ruleOut ? '✕ rule out' : 'advisory'}</span>
							</div>
						))}
					</div>
					<div className="card__foot">{ruleOutCount} of {criteria.length} criteria are hard rule-outs — a mismatch declines the deal before any AI spend, with manual override available.</div>
				</div>
				<div className="card">
					<div className="card__row" style={{ marginBottom: 12 }}><div className="card__label">How the AI scores</div><span className="card__hint" style={{ color: 'var(--dlg-pos)', fontWeight: 500 }}>= 100%</span></div>
					<div style={{ display: 'grid', gap: 10 }}>
						{weights.map((w, i) => (
							<BarRow key={w.name} label={w.name} right={`${w.pct}%`} pct={w.pct}
								color={i < 2 ? 'var(--dlg-blue)' : i < 4 ? 'var(--dlg-blue-mid)' : 'var(--dlg-blue-soft)'} />
						))}
					</div>
					<div className="card__foot">Weights drive the triage scorecard on every deal. Set in this module — Diligentsia backfills.</div>
				</div>
			</div>

			<div className="grid grid--split45">
				<div className="card">
					<div className="card__label" style={{ marginBottom: 10 }}>Subjective criteria</div>
					<div style={{ fontSize: 12.5, color: 'var(--dlg-faint)', fontWeight: 500 }}>Firm description</div>
					<div style={{ fontSize: 13, color: 'var(--dlg-ink-soft)', margin: '3px 0 10px' }}>{P.description}</div>
					<div style={{ fontSize: 12.5, color: 'var(--dlg-faint)', fontWeight: 500 }}>Investment criteria</div>
					<div style={{ fontSize: 13, color: 'var(--dlg-ink-soft)', margin: '3px 0 10px' }}>{P.investmentCriteria}</div>
					<Bubble><strong>Your thesis, as Intelligentsia reads it:</strong> "{P.thesisReadback}" <span style={{ color: 'var(--dlg-bubble-sub)' }}>Used in every deal's thesis-fit check and pitch deck analysis.</span></Bubble>
				</div>
				<div className="card">
					<div className="card__row" style={{ marginBottom: 10 }}><div className="card__label">Quick responses</div><span className="card__hint">{quickResponses.length} templates · used in decline emails</span></div>
					<div style={{ display: 'grid', gap: 6 }}>
						{quickResponses.map(q => (
							<div key={q} style={{ background: 'var(--dlg-chipfill)', borderRadius: 999, padding: '7px 14px', fontSize: 12.5, color: 'var(--dlg-ink-soft)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{q}</div>
						))}
					</div>
				</div>
			</div>

			<div className="grid grid--splitr" style={{ marginBottom: 0 }}>
				<div className="card">
					<div className="card__label" style={{ marginBottom: 12 }}>Preferred contact for leads</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
						<span style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--dlg-lavender)', color: '#26215C', fontSize: 15, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{preferredContact.initials}</span>
						<div>
							<div style={{ fontSize: 14.5, fontWeight: 500 }}>{preferredContact.name}</div>
							<div style={{ fontSize: 12.5, color: 'var(--dlg-coral)', fontWeight: 500 }}>{preferredContact.role}</div>
							<div style={{ fontSize: 12.5, color: 'var(--dlg-muted)' }}>{preferredContact.email}</div>
						</div>
						<div style={{ marginLeft: 'auto', textAlign: 'right' }}>
							<div className="factchip__label">Prompt response</div>
							<div style={{ fontSize: 17, fontWeight: 500, color: 'var(--dlg-pos)' }}>{preferredContact.promptResponse}%</div>
						</div>
					</div>
				</div>
				<div className="card" style={{ border: '1.5px dashed var(--dlg-lavender)', boxShadow: 'none' }}>
					<div className="card__row" style={{ marginBottom: 10 }}><div className="card__label">Extended criteria</div><span className="chip chip--blue" style={{ marginLeft: 'auto' }}>New fields · Diligentsia settings backfill</span></div>
					<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9, fontSize: 13 }}>
						<div><span style={{ color: 'var(--dlg-muted)' }}>Ownership target</span><div style={{ fontWeight: 500, marginTop: 2 }}>{extended.ownershipTarget}</div></div>
						<div><span style={{ color: 'var(--dlg-muted)' }}>Follow-on strategy</span><div style={{ fontWeight: 500, marginTop: 2 }}>{extended.followOn}</div></div>
						<div><span style={{ color: 'var(--dlg-muted)' }}>Business model preferences</span><div style={{ fontWeight: 500, marginTop: 2 }}>{extended.businessModels}</div></div>
						<div><span style={{ color: 'var(--dlg-muted)' }}>Keywords &amp; themes</span><div style={{ fontWeight: 500, marginTop: 2 }}>{extended.keywords}</div></div>
					</div>
				</div>
			</div>
		</main>
	);
}
